/** Clamp pdf.js render scale to a sensible range. */
export function clampViewerScale(scale: number): number {
  return Math.max(0.5, Math.min(4, Math.round(scale * 100) / 100));
}

/**
 * Pick a render scale so the page width (in PDF points) fits the viewer column
 * without CSS max-width shrinking (keeps overlay coordinates WYSIWYG).
 */
export function computeFitToWidthScale(
  pageWidthPt: number,
  containerWidthPx: number,
  paddingPx = 48
): number {
  if (pageWidthPt <= 0 || containerWidthPx <= 0) return 1;
  const available = Math.max(200, containerWidthPx - paddingPx);
  return clampViewerScale(available / pageWidthPt);
}

/**
 * Pick a render scale so the page height (in PDF points) fits the visible
 * scroll container height. Mirrors computeFitToWidthScale but for the vertical axis.
 */
export function computeFitToHeightScale(
  pageHeightPt: number,
  containerHeightPx: number,
  paddingPx = 64
): number {
  if (pageHeightPt <= 0 || containerHeightPx <= 0) return 1;
  const available = Math.max(200, containerHeightPx - paddingPx);
  return clampViewerScale(available / pageHeightPt);
}

/**
 * Zoom-step presets in ascending order. The step between adjacent presets
 * varies from ~10 % to ~25 % to feel natural across the full scale range.
 */
const ZOOM_PRESETS = [0.5, 0.67, 0.75, 0.9, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0, 4.0];

/**
 * Return the next preset scale in the given direction (+1 = zoom in, -1 = zoom out).
 * Snaps to the nearest preset that is strictly larger/smaller than the current scale.
 * Falls back to clampViewerScale when no step is available.
 */
export function nextZoomStep(scale: number, dir: 1 | -1): number {
  if (dir === 1) {
    const next = ZOOM_PRESETS.find((p) => p > scale + 0.005);
    return next !== undefined ? next : clampViewerScale(scale + 0.1);
  }
  const prev = [...ZOOM_PRESETS].reverse().find((p) => p < scale - 0.005);
  return prev !== undefined ? prev : clampViewerScale(scale - 0.1);
}

/** Convenience: the canonical "100 %" reset value. */
export const ZOOM_RESET = 1.0;